import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './GameScore.css';
import { ArrowLeft, Bird, Plus, Trees, Trash2 } from 'lucide-react';
import { eventsService, gameService } from '../../../../../services';

const emptyDriveForm = {
    driveNumber: '',
    driveName: '',
    birdsOver: '',
    birdsKilled: '',
    cartridgesUsed: '',
    windCondition: '',
    notes: ''
};

const emptyGunForm = {
    shooterName: '',
    pegNumber: '',
    birdsKilled: '',
    cartridgesUsed: '',
    notes: ''
};

const emptyBagForm = {
    speciesId: '',
    quantity: '',
    driveNumber: '',
    notes: ''
};

const emptyShooterForm = {
    eventShooterId: '',
    role: 'Gun',
    startingPeg: '',
    pegRotation: ''
};

const GameDayDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [gameDay, setGameDay] = useState(null);
    const [species, setSpecies] = useState([]);
    const [eventShooters, setEventShooters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activityMessage, setActivityMessage] = useState(null);
    const [driveForm, setDriveForm] = useState(emptyDriveForm);
    const [bagForm, setBagForm] = useState(emptyBagForm);
    const [overviewForm, setOverviewForm] = useState(null);
    const [shooterForm, setShooterForm] = useState(emptyShooterForm);
    const [gunForms, setGunForms] = useState({});
    const [editingShooterId, setEditingShooterId] = useState(null);
    const [editingShooterForm, setEditingShooterForm] = useState(emptyShooterForm);
    const [editingDriveId, setEditingDriveId] = useState(null);
    const [editingDriveForm, setEditingDriveForm] = useState(emptyDriveForm);
    const [editingGunId, setEditingGunId] = useState(null);
    const [editingGunForm, setEditingGunForm] = useState(emptyGunForm);
    const [editingBagItemId, setEditingBagItemId] = useState(null);
    const [editingBagForm, setEditingBagForm] = useState(emptyBagForm);
    const [submittingKey, setSubmittingKey] = useState(null);

    const sortedDrives = useMemo(
        () => [...(gameDay?.drives || [])].sort((a, b) => (a.driveNumber || 0) - (b.driveNumber || 0)),
        [gameDay]
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const [gameDayData, speciesData] = await Promise.all([
                    gameService.getGameDay(id),
                    gameService.getSpecies()
                ]);
                setGameDay(gameDayData);
                setOverviewForm({
                    gameType: gameDayData.gameType || '',
                    season: gameDayData.season || '',
                    estate: gameDayData.estate || '',
                    beat: gameDayData.beat || '',
                    weatherConditions: gameDayData.weatherConditions || '',
                    numberOfGuns: gameDayData.numberOfGuns ?? '',
                    numberOfDrives: gameDayData.numberOfDrives ?? '',
                    totalCartridgesUsed: gameDayData.totalCartridgesUsed ?? '',
                    notes: gameDayData.notes || ''
                });

                setSpecies(Array.isArray(speciesData) ? speciesData : []);

                if (gameDayData.eventId) {
                    const shootersData = await eventsService.getEventShooters(gameDayData.eventId);
                    setEventShooters(Array.isArray(shootersData) ? shootersData : []);
                }
            } catch (err) {
                console.error('Error loading game day:', err);
                setError('Unable to load this game day.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const setMessage = (message) => {
        setActivityMessage(message);
        setTimeout(() => setActivityMessage(null), 2500);
    };

    const handleAddDrive = async () => {
        if (!driveForm.driveNumber) {
            setError('Drive number is required.');
            return;
        }

        try {
            setSubmittingKey('drive');
            setError(null);
            const newDrive = await gameService.addDrive(id, {
                driveNumber: Number(driveForm.driveNumber),
                driveName: driveForm.driveName || null,
                birdsOver: driveForm.birdsOver === '' ? null : Number(driveForm.birdsOver),
                birdsKilled: driveForm.birdsKilled === '' ? null : Number(driveForm.birdsKilled),
                cartridgesUsed: driveForm.cartridgesUsed === '' ? null : Number(driveForm.cartridgesUsed),
                windCondition: driveForm.windCondition || null,
                notes: driveForm.notes || null
            });
            setGameDay((current) => ({
                ...current,
                drives: [...(current.drives || []), { ...newDrive, guns: [] }],
                numberOfDrives: Math.max(current.numberOfDrives || 0, (current.drives?.length || 0) + 1)
            }));
            setDriveForm(emptyDriveForm);
            setMessage('Drive added');
        } catch (submitError) {
            console.error('Error adding drive:', submitError);
            setError('Could not add drive.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleSaveOverview = async () => {
        if (!overviewForm) {
            return;
        }

        try {
            setSubmittingKey('overview');
            setError(null);
            await gameService.updateGameDay(id, {
                gameType: overviewForm.gameType || null,
                season: overviewForm.season || null,
                estate: overviewForm.estate || null,
                beat: overviewForm.beat || null,
                weatherConditions: overviewForm.weatherConditions || null,
                numberOfGuns: overviewForm.numberOfGuns === '' ? null : Number(overviewForm.numberOfGuns),
                numberOfDrives: overviewForm.numberOfDrives === '' ? null : Number(overviewForm.numberOfDrives),
                totalCartridgesUsed: overviewForm.totalCartridgesUsed === '' ? null : Number(overviewForm.totalCartridgesUsed),
                notes: overviewForm.notes || null
            });

            setGameDay((current) => {
                const nextTotalCartridges = overviewForm.totalCartridgesUsed === '' ? null : Number(overviewForm.totalCartridgesUsed);
                const totalBag = current.totalBag ?? 0;
                return {
                    ...current,
                    gameType: overviewForm.gameType,
                    season: overviewForm.season,
                    estate: overviewForm.estate,
                    beat: overviewForm.beat,
                    weatherConditions: overviewForm.weatherConditions,
                    numberOfGuns: overviewForm.numberOfGuns === '' ? null : Number(overviewForm.numberOfGuns),
                    numberOfDrives: overviewForm.numberOfDrives === '' ? null : Number(overviewForm.numberOfDrives),
                    totalCartridgesUsed: nextTotalCartridges,
                    notes: overviewForm.notes,
                    cartridgesToBirdRatio: nextTotalCartridges && totalBag > 0 ? nextTotalCartridges / totalBag : null
                };
            });
            setMessage('Game day updated');
        } catch (submitError) {
            console.error('Error updating game day:', submitError);
            setError('Could not update game day.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleAddShooter = async () => {
        if (!shooterForm.eventShooterId) {
            setError('Choose a shooter to assign.');
            return;
        }

        try {
            setSubmittingKey('shooter');
            setError(null);
            const newShooter = await gameService.addShooter(id, {
                eventShooterId: Number(shooterForm.eventShooterId),
                role: shooterForm.role || null,
                startingPeg: shooterForm.startingPeg === '' ? null : Number(shooterForm.startingPeg),
                pegRotation: shooterForm.pegRotation || null
            });
            setGameDay((current) => ({
                ...current,
                shooters: [...(current.shooters || []), newShooter]
            }));
            setShooterForm(emptyShooterForm);
            setMessage('Shooter assigned');
        } catch (submitError) {
            console.error('Error adding shooter:', submitError);
            setError('Could not assign shooter.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleDeleteShooter = async (gameDayShooterId) => {
        if (!window.confirm('Remove this shooter from the game day?')) {
            return;
        }

        try {
            await gameService.deleteShooter(id, gameDayShooterId);

            setGameDay((current) => ({
                ...current,
                shooters: (current.shooters || []).filter((shooter) => shooter.gameDayShooterId !== gameDayShooterId)
            }));
            setMessage('Shooter removed');
        } catch (deleteError) {
            console.error('Error deleting shooter:', deleteError);
            setError('Could not remove shooter.');
        }
    };

    const startEditingShooter = (shooter) => {
        setEditingShooterId(shooter.gameDayShooterId);
        setEditingShooterForm({
            eventShooterId: shooter.eventShooterId ?? '',
            role: shooter.role || 'Gun',
            startingPeg: shooter.startingPeg ?? '',
            pegRotation: shooter.pegRotation || ''
        });
    };

    const handleSaveShooter = async (gameDayShooterId) => {
        try {
            setSubmittingKey(`edit-shooter-${gameDayShooterId}`);
            setError(null);
            await gameService.updateShooter(id, gameDayShooterId, {
                role: editingShooterForm.role || null,
                startingPeg: editingShooterForm.startingPeg === '' ? null : Number(editingShooterForm.startingPeg),
                pegRotation: editingShooterForm.pegRotation || null
            });

            setGameDay((current) => ({
                ...current,
                shooters: (current.shooters || []).map((shooter) =>
                    shooter.gameDayShooterId === gameDayShooterId
                        ? {
                            ...shooter,
                            role: editingShooterForm.role || null,
                            startingPeg: editingShooterForm.startingPeg === '' ? null : Number(editingShooterForm.startingPeg),
                            pegRotation: editingShooterForm.pegRotation || null
                        }
                        : shooter
                )
            }));
            setEditingShooterId(null);
            setEditingShooterForm(emptyShooterForm);
            setMessage('Shooter updated');
        } catch (saveError) {
            console.error('Error updating shooter:', saveError);
            setError('Could not update shooter.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleDeleteDrive = async (driveId) => {
        if (!window.confirm('Delete this drive and its gun records?')) {
            return;
        }

        try {
            await gameService.deleteDrive(id, driveId);

            setGameDay((current) => ({
                ...current,
                drives: (current.drives || []).filter((drive) => drive.gameDriveId !== driveId)
            }));
            setMessage('Drive deleted');
        } catch (deleteError) {
            console.error('Error deleting drive:', deleteError);
            setError('Could not delete drive.');
        }
    };

    const startEditingDrive = (drive) => {
        setEditingDriveId(drive.gameDriveId);
        setEditingDriveForm({
            driveNumber: drive.driveNumber ?? '',
            driveName: drive.driveName || '',
            birdsOver: drive.birdsOver ?? '',
            birdsKilled: drive.birdsKilled ?? '',
            cartridgesUsed: drive.cartridgesUsed ?? '',
            windCondition: drive.windCondition || '',
            notes: drive.notes || ''
        });
    };

    const handleSaveDrive = async (driveId) => {
        try {
            setSubmittingKey(`edit-drive-${driveId}`);
            setError(null);
            await gameService.updateDrive(id, driveId, {
                driveName: editingDriveForm.driveName || null,
                birdsOver: editingDriveForm.birdsOver === '' ? null : Number(editingDriveForm.birdsOver),
                birdsKilled: editingDriveForm.birdsKilled === '' ? null : Number(editingDriveForm.birdsKilled),
                cartridgesUsed: editingDriveForm.cartridgesUsed === '' ? null : Number(editingDriveForm.cartridgesUsed),
                windCondition: editingDriveForm.windCondition || null,
                notes: editingDriveForm.notes || null
            });

            setGameDay((current) => ({
                ...current,
                drives: current.drives.map((drive) =>
                    drive.gameDriveId === driveId
                        ? {
                            ...drive,
                            driveName: editingDriveForm.driveName || null,
                            birdsOver: editingDriveForm.birdsOver === '' ? null : Number(editingDriveForm.birdsOver),
                            birdsKilled: editingDriveForm.birdsKilled === '' ? null : Number(editingDriveForm.birdsKilled),
                            cartridgesUsed: editingDriveForm.cartridgesUsed === '' ? null : Number(editingDriveForm.cartridgesUsed),
                            windCondition: editingDriveForm.windCondition || null,
                            notes: editingDriveForm.notes || null
                        }
                        : drive
                )
            }));
            setEditingDriveId(null);
            setEditingDriveForm(emptyDriveForm);
            setMessage('Drive updated');
        } catch (saveError) {
            console.error('Error updating drive:', saveError);
            setError('Could not update drive.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleGunFormChange = (driveId, field, value) => {
        setGunForms((current) => ({
            ...current,
            [driveId]: {
                ...(current[driveId] || emptyGunForm),
                [field]: value
            }
        }));
    };

    const handleAddGun = async (driveId) => {
        const form = gunForms[driveId] || emptyGunForm;
        if (!form.shooterName || !form.pegNumber) {
            setError('Shooter name and peg number are required for drive gun entries.');
            return;
        }

        try {
            setSubmittingKey(`gun-${driveId}`);
            setError(null);
            const newGun = await gameService.addGun(id, driveId, {
                shooterName: form.shooterName,
                pegNumber: Number(form.pegNumber),
                birdsKilled: form.birdsKilled === '' ? null : Number(form.birdsKilled),
                cartridgesUsed: form.cartridgesUsed === '' ? null : Number(form.cartridgesUsed),
                notes: form.notes || null
            });
            setGameDay((current) => ({
                ...current,
                drives: current.drives.map((drive) =>
                    drive.gameDriveId === driveId
                        ? { ...drive, guns: [...(drive.guns || []), newGun] }
                        : drive
                )
            }));
            setGunForms((current) => ({ ...current, [driveId]: emptyGunForm }));
            setMessage('Gun entry added');
        } catch (submitError) {
            console.error('Error adding drive gun:', submitError);
            setError('Could not add gun entry.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleDeleteGun = async (driveId, gunId) => {
        if (!window.confirm('Delete this gun entry?')) {
            return;
        }

        try {
            await gameService.deleteGun(id, driveId, gunId);

            setGameDay((current) => ({
                ...current,
                drives: current.drives.map((drive) =>
                    drive.gameDriveId === driveId
                        ? { ...drive, guns: (drive.guns || []).filter((gun) => gun.gameDriveGunId !== gunId) }
                        : drive
                )
            }));
            setMessage('Gun entry deleted');
        } catch (deleteError) {
            console.error('Error deleting gun entry:', deleteError);
            setError('Could not delete gun entry.');
        }
    };

    const startEditingGun = (gun) => {
        setEditingGunId(gun.gameDriveGunId);
        setEditingGunForm({
            shooterName: gun.shooterName || '',
            pegNumber: gun.pegNumber ?? '',
            birdsKilled: gun.birdsKilled ?? '',
            cartridgesUsed: gun.cartridgesUsed ?? '',
            notes: gun.notes || ''
        });
    };

    const handleSaveGun = async (driveId, gunId) => {
        try {
            setSubmittingKey(`edit-gun-${gunId}`);
            setError(null);
            await gameService.updateGun(id, driveId, gunId, {
                shooterName: editingGunForm.shooterName || null,
                pegNumber: editingGunForm.pegNumber === '' ? null : Number(editingGunForm.pegNumber),
                birdsKilled: editingGunForm.birdsKilled === '' ? null : Number(editingGunForm.birdsKilled),
                cartridgesUsed: editingGunForm.cartridgesUsed === '' ? null : Number(editingGunForm.cartridgesUsed),
                notes: editingGunForm.notes || null
            });

            setGameDay((current) => ({
                ...current,
                drives: current.drives.map((drive) =>
                    drive.gameDriveId === driveId
                        ? {
                            ...drive,
                            guns: (drive.guns || []).map((gun) =>
                                gun.gameDriveGunId === gunId
                                    ? {
                                        ...gun,
                                        shooterName: editingGunForm.shooterName || null,
                                        pegNumber: editingGunForm.pegNumber === '' ? null : Number(editingGunForm.pegNumber),
                                        birdsKilled: editingGunForm.birdsKilled === '' ? null : Number(editingGunForm.birdsKilled),
                                        cartridgesUsed: editingGunForm.cartridgesUsed === '' ? null : Number(editingGunForm.cartridgesUsed),
                                        notes: editingGunForm.notes || null
                                    }
                                    : gun
                            )
                        }
                        : drive
                )
            }));
            setEditingGunId(null);
            setEditingGunForm(emptyGunForm);
            setMessage('Gun entry updated');
        } catch (saveError) {
            console.error('Error updating gun entry:', saveError);
            setError('Could not update gun entry.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleAddBagItem = async () => {
        if (!bagForm.speciesId || !bagForm.quantity) {
            setError('Species and quantity are required.');
            return;
        }

        try {
            setSubmittingKey('bag');
            setError(null);
            const newBagItem = await gameService.addBagItem(id, {
                speciesId: Number(bagForm.speciesId),
                quantity: Number(bagForm.quantity),
                driveNumber: bagForm.driveNumber === '' ? null : Number(bagForm.driveNumber),
                notes: bagForm.notes || null
            });
            setGameDay((current) => ({
                ...current,
                bagItems: [...(current.bagItems || []), newBagItem],
                totalBag: (current.totalBag || 0) + newBagItem.quantity
            }));
            setBagForm(emptyBagForm);
            setMessage('Bag item added');
        } catch (submitError) {
            console.error('Error adding bag item:', submitError);
            setError('Could not add bag item.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleDeleteBagItem = async (bagItemId) => {
        if (!window.confirm('Delete this bag item?')) {
            return;
        }

        try {
            await gameService.deleteBagItem(id, bagItemId);

            setGameDay((current) => {
                const remaining = (current.bagItems || []).filter((item) => item.gameBagItemId !== bagItemId);
                return {
                    ...current,
                    bagItems: remaining,
                    totalBag: remaining.reduce((total, item) => total + (item.quantity || 0), 0)
                };
            });
            setMessage('Bag item deleted');
        } catch (deleteError) {
            console.error('Error deleting bag item:', deleteError);
            setError('Could not delete bag item.');
        }
    };

    const startEditingBagItem = (item) => {
        setEditingBagItemId(item.gameBagItemId);
        setEditingBagForm({
            speciesId: item.speciesId ?? '',
            quantity: item.quantity ?? '',
            driveNumber: item.driveNumber ?? '',
            notes: item.notes || ''
        });
    };

    const handleSaveBagItem = async (bagItemId) => {
        try {
            setSubmittingKey(`edit-bag-${bagItemId}`);
            setError(null);
            await gameService.updateBagItem(id, bagItemId, {
                speciesId: editingBagForm.speciesId === '' ? null : Number(editingBagForm.speciesId),
                quantity: editingBagForm.quantity === '' ? null : Number(editingBagForm.quantity),
                driveNumber: editingBagForm.driveNumber === '' ? null : Number(editingBagForm.driveNumber),
                notes: editingBagForm.notes || null
            });

            setGameDay((current) => {
                const updatedBagItems = (current.bagItems || []).map((item) =>
                    item.gameBagItemId === bagItemId
                        ? {
                            ...item,
                            speciesId: editingBagForm.speciesId === '' ? null : Number(editingBagForm.speciesId),
                            speciesName: species.find((entry) => entry.speciesId === Number(editingBagForm.speciesId))?.name || item.speciesName,
                            quantity: editingBagForm.quantity === '' ? null : Number(editingBagForm.quantity),
                            driveNumber: editingBagForm.driveNumber === '' ? null : Number(editingBagForm.driveNumber),
                            notes: editingBagForm.notes || null
                        }
                        : item
                );
                return {
                    ...current,
                    bagItems: updatedBagItems,
                    totalBag: updatedBagItems.reduce((total, item) => total + (item.quantity || 0), 0)
                };
            });
            setEditingBagItemId(null);
            setEditingBagForm(emptyBagForm);
            setMessage('Bag item updated');
        } catch (saveError) {
            console.error('Error updating bag item:', saveError);
            setError('Could not update bag item.');
        } finally {
            setSubmittingKey(null);
        }
    };

    if (isLoading) {
        return (
            <div className="game-loading">
                <div className="game-spinner"></div>
                <p>Loading game day...</p>
            </div>
        );
    }

    if (error && !gameDay) {
        return <div className="game-error-message">{error}</div>;
    }

    if (!gameDay) {
        return <div className="game-error-message">Game day not found.</div>;
    }

    return (
        <div className="game-score-page">
            <div className="game-detail-actions">
                <button className="game-secondary-btn" onClick={() => navigate('/dashboard/score/game')}>
                    <ArrowLeft size={16} />
                    Back to game dashboard
                </button>
            </div>

            <div className="game-header">
                <div className="game-header-content">
                    <div className="game-header-icon">
                        <Trees size={28} />
                    </div>
                    <div className="game-header-text">
                        <h1>{gameDay.eventName || 'Game day'}</h1>
                        <p>{gameDay.gameType || 'Game'} at {gameDay.location || 'venue to be confirmed'}{gameDay.estate ? ` · ${gameDay.estate}` : ''}</p>
                    </div>
                </div>
            </div>

            {error ? <div className="game-error-message">{error}</div> : null}
            {activityMessage ? <div className="game-success-message">{activityMessage}</div> : null}

            <div className="game-detail-grid">
                <section className="game-detail-card">
                    <h3>Overview</h3>
                    <p className="game-section-note">The event venue stays as the main location. Use the estate or shoot location field only when the day needs an extra location detail layered on top.</p>
                    <div className="game-form-grid">
                        <input
                            type="text"
                            placeholder="Game type"
                            value={overviewForm?.gameType || ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, gameType: event.target.value }))}
                        />
                        <input
                            type="text"
                            placeholder="Season"
                            value={overviewForm?.season || ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, season: event.target.value }))}
                        />
                        <input
                            type="text"
                            placeholder="Estate / shoot location (optional)"
                            value={overviewForm?.estate || ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, estate: event.target.value }))}
                        />
                        <input
                            type="text"
                            placeholder="Beat"
                            value={overviewForm?.beat || ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, beat: event.target.value }))}
                        />
                        <input
                            type="number"
                            min="0"
                            placeholder="No. of guns"
                            value={overviewForm?.numberOfGuns ?? ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, numberOfGuns: event.target.value }))}
                        />
                        <input
                            type="number"
                            min="0"
                            placeholder="Planned drives"
                            value={overviewForm?.numberOfDrives ?? ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, numberOfDrives: event.target.value }))}
                        />
                        <input
                            type="number"
                            min="0"
                            placeholder="Total cartridges"
                            value={overviewForm?.totalCartridgesUsed ?? ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, totalCartridgesUsed: event.target.value }))}
                        />
                        <input
                            type="text"
                            placeholder="Weather"
                            value={overviewForm?.weatherConditions || ''}
                            onChange={(event) => setOverviewForm((current) => ({ ...current, weatherConditions: event.target.value }))}
                        />
                    </div>
                    <textarea
                        className="game-form-textarea"
                        placeholder="Day notes"
                        value={overviewForm?.notes || ''}
                        onChange={(event) => setOverviewForm((current) => ({ ...current, notes: event.target.value }))}
                    />
                    <button className="game-primary-btn" onClick={handleSaveOverview} disabled={submittingKey === 'overview'}>
                        <Plus size={16} />
                        {submittingKey === 'overview' ? 'Saving...' : 'Save overview'}
                    </button>
                    <div className="game-detail-list">
                        <p><strong>Date:</strong> {gameDay.eventDate ? new Date(gameDay.eventDate).toLocaleDateString() : 'Not set'}</p>
                        <p><strong>Total bag:</strong> {gameDay.totalBag ?? 0}</p>
                        <p><strong>Cartridges:</strong> {gameDay.totalCartridgesUsed ?? 0}</p>
                        <p><strong>Ratio:</strong> {gameDay.cartridgesToBirdRatio?.toFixed(2) || 'N/A'} : 1</p>
                    </div>
                </section>

                <section className="game-detail-card">
                    <h3>Shooters</h3>
                    <div className="game-inline-form">
                        <select
                            value={shooterForm.eventShooterId}
                            onChange={(event) => setShooterForm((current) => ({ ...current, eventShooterId: event.target.value }))}
                        >
                            <option value="">Select event shooter</option>
                            {eventShooters
                                .filter((eventShooter) => !(gameDay.shooters || []).some((assigned) => assigned.eventShooterId === eventShooter.eventShooterId))
                                .map((eventShooter) => (
                                    <option key={eventShooter.eventShooterId} value={eventShooter.eventShooterId}>
                                        {eventShooter.shooterName || `${eventShooter.firstName || ''} ${eventShooter.lastName || ''}`.trim() || eventShooter.userName}
                                    </option>
                                ))}
                        </select>
                        <select
                            value={shooterForm.role}
                            onChange={(event) => setShooterForm((current) => ({ ...current, role: event.target.value }))}
                        >
                            {['Gun', 'PickerUp', 'Loader', 'Beater', 'Keeper', 'Host'].map((role) => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                        </select>
                        <input
                            type="number"
                            min="1"
                            placeholder="Starting peg"
                            value={shooterForm.startingPeg}
                            onChange={(event) => setShooterForm((current) => ({ ...current, startingPeg: event.target.value }))}
                        />
                        <input
                            type="text"
                            placeholder="Peg rotation"
                            value={shooterForm.pegRotation}
                            onChange={(event) => setShooterForm((current) => ({ ...current, pegRotation: event.target.value }))}
                        />
                        <button className="game-primary-btn" onClick={handleAddShooter} disabled={submittingKey === 'shooter'}>
                            <Plus size={16} />
                            {submittingKey === 'shooter' ? 'Assigning...' : 'Assign shooter'}
                        </button>
                    </div>
                    {gameDay.shooters?.length ? (
                        <div className="game-detail-stack">
                            {gameDay.shooters.map((shooter) => (
                                <div key={shooter.gameDayShooterId} className="game-detail-row">
                                    <span>{shooter.shooterName || 'Unnamed shooter'}</span>
                                    {editingShooterId === shooter.gameDayShooterId ? (
                                        <div className="game-inline-form-compact">
                                            <select
                                                value={editingShooterForm.role}
                                                onChange={(event) => setEditingShooterForm((current) => ({ ...current, role: event.target.value }))}
                                            >
                                                {['Gun', 'PickerUp', 'Loader', 'Beater', 'Keeper', 'Host'].map((role) => (
                                                    <option key={role} value={role}>{role}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                min="1"
                                                placeholder="Starting peg"
                                                value={editingShooterForm.startingPeg}
                                                onChange={(event) => setEditingShooterForm((current) => ({ ...current, startingPeg: event.target.value }))}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Peg rotation"
                                                value={editingShooterForm.pegRotation}
                                                onChange={(event) => setEditingShooterForm((current) => ({ ...current, pegRotation: event.target.value }))}
                                            />
                                            <button
                                                className="game-secondary-btn"
                                                onClick={() => handleSaveShooter(shooter.gameDayShooterId)}
                                                disabled={submittingKey === `edit-shooter-${shooter.gameDayShooterId}`}
                                            >
                                                {submittingKey === `edit-shooter-${shooter.gameDayShooterId}` ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                className="game-secondary-btn"
                                                onClick={() => {
                                                    setEditingShooterId(null);
                                                    setEditingShooterForm(emptyShooterForm);
                                                }}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="game-detail-meta">
                                                <span>{shooter.role || 'Participant'}</span>
                                                {shooter.startingPeg ? <span>Peg {shooter.startingPeg}</span> : null}
                                                {shooter.pegRotation ? <span>{shooter.pegRotation}</span> : null}
                                            </div>
                                            <div className="game-subcard-actions">
                                                <button className="game-secondary-btn" onClick={() => startEditingShooter(shooter)}>
                                                    Edit shooter
                                                </button>
                                                <button className="game-icon-btn" onClick={() => handleDeleteShooter(shooter.gameDayShooterId)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="game-detail-empty">No shooters assigned yet.</p>
                    )}
                </section>
            </div>

            <section className="game-detail-card">
                <div className="game-section-header">
                    <h3>Drives</h3>
                </div>

                <div className="game-form-grid">
                    <input
                        type="number"
                        min="1"
                        placeholder="Drive no."
                        value={driveForm.driveNumber}
                        onChange={(event) => setDriveForm((current) => ({ ...current, driveNumber: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Drive name"
                        value={driveForm.driveName}
                        onChange={(event) => setDriveForm((current) => ({ ...current, driveName: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Birds over"
                        value={driveForm.birdsOver}
                        onChange={(event) => setDriveForm((current) => ({ ...current, birdsOver: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Birds bagged"
                        value={driveForm.birdsKilled}
                        onChange={(event) => setDriveForm((current) => ({ ...current, birdsKilled: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="0"
                        placeholder="Cartridges"
                        value={driveForm.cartridgesUsed}
                        onChange={(event) => setDriveForm((current) => ({ ...current, cartridgesUsed: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Wind / notes"
                        value={driveForm.windCondition}
                        onChange={(event) => setDriveForm((current) => ({ ...current, windCondition: event.target.value }))}
                    />
                </div>
                <textarea
                    className="game-form-textarea"
                    placeholder="Drive notes"
                    value={driveForm.notes}
                    onChange={(event) => setDriveForm((current) => ({ ...current, notes: event.target.value }))}
                />
                <button className="game-primary-btn" onClick={handleAddDrive} disabled={submittingKey === 'drive'}>
                    <Plus size={16} />
                    {submittingKey === 'drive' ? 'Adding drive...' : 'Add drive'}
                </button>

                {sortedDrives.length ? (
                    <div className="game-detail-stack game-stack-gap-lg">
                        {sortedDrives.map((drive) => {
                            const gunForm = gunForms[drive.gameDriveId] || emptyGunForm;
                            return (
                                <div key={drive.gameDriveId} className="game-subcard">
                                    <div className="game-detail-row game-detail-row-wrap">
                                        <div>
                                            <strong>Drive {drive.driveNumber}</strong>
                                            <p>{drive.driveName || 'Unnamed drive'}</p>
                                        </div>
                                        <div className="game-detail-meta">
                                            <span>Over {drive.birdsOver ?? 0}</span>
                                            <span>Bagged {drive.birdsKilled ?? 0}</span>
                                            <span>Cartridges {drive.cartridgesUsed ?? 0}</span>
                                        </div>
                                        <button className="game-icon-btn" onClick={() => handleDeleteDrive(drive.gameDriveId)}>
                                            <Trash2 size={16} />
                                        </button>
                                    </div>

                                    {editingDriveId === drive.gameDriveId ? (
                                        <div className="game-form-grid">
                                            <input
                                                type="text"
                                                placeholder="Drive name"
                                                value={editingDriveForm.driveName}
                                                onChange={(event) => setEditingDriveForm((current) => ({ ...current, driveName: event.target.value }))}
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="Birds over"
                                                value={editingDriveForm.birdsOver}
                                                onChange={(event) => setEditingDriveForm((current) => ({ ...current, birdsOver: event.target.value }))}
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="Birds bagged"
                                                value={editingDriveForm.birdsKilled}
                                                onChange={(event) => setEditingDriveForm((current) => ({ ...current, birdsKilled: event.target.value }))}
                                            />
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="Cartridges"
                                                value={editingDriveForm.cartridgesUsed}
                                                onChange={(event) => setEditingDriveForm((current) => ({ ...current, cartridgesUsed: event.target.value }))}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Wind"
                                                value={editingDriveForm.windCondition}
                                                onChange={(event) => setEditingDriveForm((current) => ({ ...current, windCondition: event.target.value }))}
                                            />
                                            <input
                                                type="text"
                                                placeholder="Notes"
                                                value={editingDriveForm.notes}
                                                onChange={(event) => setEditingDriveForm((current) => ({ ...current, notes: event.target.value }))}
                                            />
                                            <button
                                                className="game-primary-btn"
                                                onClick={() => handleSaveDrive(drive.gameDriveId)}
                                                disabled={submittingKey === `edit-drive-${drive.gameDriveId}`}
                                            >
                                                {submittingKey === `edit-drive-${drive.gameDriveId}` ? 'Saving...' : 'Save drive'}
                                            </button>
                                            <button className="game-secondary-btn" onClick={() => setEditingDriveId(null)}>
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="game-subcard-actions">
                                            <button className="game-secondary-btn" onClick={() => startEditingDrive(drive)}>
                                                Edit drive
                                            </button>
                                        </div>
                                    )}

                                    <div className="game-inline-form">
                                        <input
                                            type="text"
                                            placeholder="Shooter name"
                                            value={gunForm.shooterName}
                                            onChange={(event) => handleGunFormChange(drive.gameDriveId, 'shooterName', event.target.value)}
                                        />
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="Peg"
                                            value={gunForm.pegNumber}
                                            onChange={(event) => handleGunFormChange(drive.gameDriveId, 'pegNumber', event.target.value)}
                                        />
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="Birds"
                                            value={gunForm.birdsKilled}
                                            onChange={(event) => handleGunFormChange(drive.gameDriveId, 'birdsKilled', event.target.value)}
                                        />
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="Cartridges"
                                            value={gunForm.cartridgesUsed}
                                            onChange={(event) => handleGunFormChange(drive.gameDriveId, 'cartridgesUsed', event.target.value)}
                                        />
                                        <button
                                            className="game-primary-btn"
                                            onClick={() => handleAddGun(drive.gameDriveId)}
                                            disabled={submittingKey === `gun-${drive.gameDriveId}`}
                                        >
                                            <Plus size={16} />
                                            Add gun
                                        </button>
                                    </div>

                                    {(drive.guns || []).length ? (
                                        <div className="game-detail-stack">
                                            {drive.guns
                                                .slice()
                                                .sort((a, b) => (a.pegNumber || 0) - (b.pegNumber || 0))
                                                .map((gun) => (
                                                    <div key={gun.gameDriveGunId} className="game-gun-block">
                                                        <div className="game-detail-row">
                                                            <span>{gun.shooterName || 'Unnamed gun'} - Peg {gun.pegNumber}</span>
                                                            <div className="game-detail-meta">
                                                                <span>Birds {gun.birdsKilled ?? 0}</span>
                                                                <span>Cartridges {gun.cartridgesUsed ?? 0}</span>
                                                            </div>
                                                            <button
                                                                className="game-icon-btn"
                                                                onClick={() => handleDeleteGun(drive.gameDriveId, gun.gameDriveGunId)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                        {editingGunId === gun.gameDriveGunId ? (
                                                            <div className="game-inline-form game-inline-form-compact">
                                                                <input
                                                                    type="text"
                                                                    placeholder="Shooter name"
                                                                    value={editingGunForm.shooterName}
                                                                    onChange={(event) => setEditingGunForm((current) => ({ ...current, shooterName: event.target.value }))}
                                                                />
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    placeholder="Peg"
                                                                    value={editingGunForm.pegNumber}
                                                                    onChange={(event) => setEditingGunForm((current) => ({ ...current, pegNumber: event.target.value }))}
                                                                />
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    placeholder="Birds"
                                                                    value={editingGunForm.birdsKilled}
                                                                    onChange={(event) => setEditingGunForm((current) => ({ ...current, birdsKilled: event.target.value }))}
                                                                />
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    placeholder="Cartridges"
                                                                    value={editingGunForm.cartridgesUsed}
                                                                    onChange={(event) => setEditingGunForm((current) => ({ ...current, cartridgesUsed: event.target.value }))}
                                                                />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Notes"
                                                                    value={editingGunForm.notes}
                                                                    onChange={(event) => setEditingGunForm((current) => ({ ...current, notes: event.target.value }))}
                                                                />
                                                                <button
                                                                    className="game-primary-btn"
                                                                    onClick={() => handleSaveGun(drive.gameDriveId, gun.gameDriveGunId)}
                                                                    disabled={submittingKey === `edit-gun-${gun.gameDriveGunId}`}
                                                                >
                                                                    {submittingKey === `edit-gun-${gun.gameDriveGunId}` ? 'Saving...' : 'Save gun'}
                                                                </button>
                                                                <button className="game-secondary-btn" onClick={() => setEditingGunId(null)}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="game-subcard-actions">
                                                                <button className="game-secondary-btn" onClick={() => startEditingGun(gun)}>
                                                                    Edit gun
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    ) : (
                                        <p className="game-detail-empty">No gun entries for this drive yet.</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="game-detail-empty">No drives recorded yet.</p>
                )}
            </section>

            <section className="game-detail-card">
                <h3>Bag</h3>
                <div className="game-inline-form">
                    <select
                        value={bagForm.speciesId}
                        onChange={(event) => setBagForm((current) => ({ ...current, speciesId: event.target.value }))}
                    >
                        <option value="">Select species</option>
                        {species.map((speciesItem) => (
                            <option key={speciesItem.speciesId} value={speciesItem.speciesId}>
                                {speciesItem.name}
                            </option>
                        ))}
                    </select>
                    <input
                        type="number"
                        min="1"
                        placeholder="Qty"
                        value={bagForm.quantity}
                        onChange={(event) => setBagForm((current) => ({ ...current, quantity: event.target.value }))}
                    />
                    <input
                        type="number"
                        min="1"
                        placeholder="Drive no."
                        value={bagForm.driveNumber}
                        onChange={(event) => setBagForm((current) => ({ ...current, driveNumber: event.target.value }))}
                    />
                    <input
                        type="text"
                        placeholder="Notes"
                        value={bagForm.notes}
                        onChange={(event) => setBagForm((current) => ({ ...current, notes: event.target.value }))}
                    />
                    <button className="game-primary-btn" onClick={handleAddBagItem} disabled={submittingKey === 'bag'}>
                        <Plus size={16} />
                        {submittingKey === 'bag' ? 'Adding...' : 'Add bag item'}
                    </button>
                </div>

                {gameDay.bagItems?.length ? (
                    <div className="game-detail-stack">
                        {gameDay.bagItems.map((item) => (
                            <div key={item.gameBagItemId} className="game-gun-block">
                                <div className="game-detail-row">
                                    <span><Bird size={14} /> {item.speciesName || 'Species'}</span>
                                    <div className="game-detail-meta">
                                        <span>Qty {item.quantity}</span>
                                        {item.driveNumber ? <span>Drive {item.driveNumber}</span> : null}
                                    </div>
                                    <button className="game-icon-btn" onClick={() => handleDeleteBagItem(item.gameBagItemId)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                                {editingBagItemId === item.gameBagItemId ? (
                                    <div className="game-inline-form game-inline-form-compact">
                                        <select
                                            value={editingBagForm.speciesId}
                                            onChange={(event) => setEditingBagForm((current) => ({ ...current, speciesId: event.target.value }))}
                                        >
                                            <option value="">Select species</option>
                                            {species.map((speciesItem) => (
                                                <option key={speciesItem.speciesId} value={speciesItem.speciesId}>
                                                    {speciesItem.name}
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="Qty"
                                            value={editingBagForm.quantity}
                                            onChange={(event) => setEditingBagForm((current) => ({ ...current, quantity: event.target.value }))}
                                        />
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="Drive no."
                                            value={editingBagForm.driveNumber}
                                            onChange={(event) => setEditingBagForm((current) => ({ ...current, driveNumber: event.target.value }))}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Notes"
                                            value={editingBagForm.notes}
                                            onChange={(event) => setEditingBagForm((current) => ({ ...current, notes: event.target.value }))}
                                        />
                                        <button
                                            className="game-primary-btn"
                                            onClick={() => handleSaveBagItem(item.gameBagItemId)}
                                            disabled={submittingKey === `edit-bag-${item.gameBagItemId}`}
                                        >
                                            {submittingKey === `edit-bag-${item.gameBagItemId}` ? 'Saving...' : 'Save bag item'}
                                        </button>
                                        <button className="game-secondary-btn" onClick={() => setEditingBagItemId(null)}>
                                            Cancel
                                        </button>
                                    </div>
                                ) : (
                                    <div className="game-subcard-actions">
                                        <button className="game-secondary-btn" onClick={() => startEditingBagItem(item)}>
                                            Edit bag item
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="game-detail-empty">No bag items recorded yet.</p>
                )}
            </section>
        </div>
    );
};

export default GameDayDetail;
